import { useQuery, useQueryClient, useMutation } from "react-query";
import { useDispatch } from "react-redux";
import { uiActions } from "store";
import {
	ErrorResponseDto,
	DocumentsMutationOptions,
	DocumentMediaMutation,
	SortBy,
} from "types";
import { DocumentsServiceContextType } from "./documentsServiceContext";
import { insertDocument } from "./documentsCacheHelpers";

export type UseAttachmentServiceOptions = DocumentsServiceContextType;

export type UseMutationParams<TSuccess = any, TError = ErrorResponseDto> = {
	onSuccessCb?: (result?: TSuccess) => any;
	onErrorCb?: (error?: TError) => void;
};

const useDocumentsService = ({ key, service }: UseAttachmentServiceOptions) => {
	const dispatch = useDispatch();
	const queryClient = useQueryClient();

	const handleShowError = (error: ErrorResponseDto) => {
		dispatch(
			uiActions.showErrorNotification({
				title: error?.Message,
			}),
		);
	};

	const useDocumentsListQuery = ({
		entityId,
		page,
		sortBy,
	}: {
		entityId: number;
		page: number;
		sortBy: SortBy | undefined;
	}) => {
		const fetchDocuments = () => {
			return service.getDocuments(entityId, { page }, sortBy);
		};

		const queryResult = useQuery([key, page, sortBy], fetchDocuments, {
			keepPreviousData: true,
			enabled: !!entityId,
		});

		return { ...queryResult };
	};

	const useAddDocumentMutation = (
		page: number,
		sortBy: SortBy | undefined,
		{ onErrorCb }: UseMutationParams = {},
	) => {
		return useMutation(
			({ entityId, document }: DocumentsMutationOptions) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.addDocument(entityId, document);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
				onSuccess(newDocument) {
					dispatch(
						uiActions.showSuccessNotification({
							title: "Document successfully added",
						}),
					);

					queryClient.setQueryData<any>(
						[key, page, sortBy],
						(oldData: any) => {
							if (oldData?.items) {
								return {
									...oldData,
									count: oldData?.count + 1,
									items: insertDocument(
										oldData?.items,
										newDocument,
										!sortBy?.desc,
									),
								};
							} else {
								return insertDocument(oldData, newDocument);
							}
						},
					);
				},
				onError(error: ErrorResponseDto) {
					handleShowError(error);
					onErrorCb?.(error);
				},
			},
		);
	};

	const useViewDocumentMutation = () => {
		return useMutation(
			({ entityId, documentId }: DocumentMediaMutation) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.viewDocument(entityId, documentId);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
			},
		);
	};

	const useDownloadDocumentMutation = () => {
		return useMutation(
			({ entityId, documentId }: DocumentMediaMutation) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.downloadDocument(entityId, documentId);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
			},
		);
	};

	const useDeleteDocumentMutation = (refetch: Function) => {
		return useMutation(
			({ entityId, documentId }: DocumentMediaMutation) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.deleteDocument(entityId, documentId);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
				onSuccess() {
					dispatch(
						uiActions.showSuccessNotification({
							title: "Document successfully deleted",
						}),
					);
					refetch();
				},
				onError(error: ErrorResponseDto) {
					handleShowError(error);
				},
			},
		);
	};

	return {
		useDocumentsListQuery,
		useAddDocumentMutation,
		useDeleteDocumentMutation,
		useViewDocumentMutation,
		useDownloadDocumentMutation,
	};
};

export default useDocumentsService;

